/**
 * Refresh Stage 7: Targeted Research
 *
 * Non-LLM stage that runs Tavily searches for sections flagged as
 * research_needed in the brief. Same pattern as creation Stage4aResearchV2.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const brief = (document as any)?.refresh?.brief;
  const sections = brief?.sections || [];
  const researchSections = sections.filter((s: any) =>
    s.research_needed || (s.subsections || []).some((sub: any) => sub.research_needed)
  );

  return (
    <div className="text-xs text-gray-600 bg-gray-50 rounded border p-2 space-y-1">
      <div>
        Sections needing research: {researchSections.length} of {sections.length}
      </div>
      {researchSections.map((s: any) => (
        <div key={s.id} className="pl-2 text-gray-500">
          {s.heading || '(intro)'} — {(s.research_queries || []).length} queries
        </div>
      ))}
      {researchSections.length === 0 && (
        <div className="text-gray-400">No sections flagged for research in the brief.</div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const sectionResearch = doc.content_research?.section_research;
  if (!sectionResearch || Object.keys(sectionResearch).length === 0) {
    return <div className="text-sm text-gray-500">No research results yet.</div>;
  }
  const briefSections = (doc as any)?.refresh?.brief?.sections || [];

  return (
    <div className="space-y-3">
      <div className="text-xs font-medium text-gray-700 mb-1">
        Research results ({Object.keys(sectionResearch).length} sections)
      </div>
      {Object.entries(sectionResearch).map(([sectionId, results]) => {
        const section = briefSections.find((s: any) => s.id === sectionId);
        const resultList = results as any[];
        return (
          <div key={sectionId} className="border rounded p-2 text-xs">
            <div className="font-medium text-gray-800 mb-1">
              {section?.heading || sectionId}
            </div>
            {resultList.length > 0 ? (
              <div className="space-y-1 ml-2">
                {resultList.map((r: any, i: number) => (
                  <div key={i} className="text-gray-600">
                    <span className="font-medium">{r.title}</span>
                    {r.url && <span className="text-blue-500 ml-1">{r.url}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400 ml-2">No results found.</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function RefreshResearchV2() {
  const { document } = useArticleContext();
  const brief = (document as any)?.refresh?.brief;
  const hasResearchNeeded = (brief?.sections || []).some((s: any) =>
    s.research_needed || (s.subsections || []).some((sub: any) => sub.research_needed)
  );

  return (
    <StagePanel
      stageId="refresh_7_research"
      title="Refresh: Research"
      description="Runs Tavily searches for sections flagged as needing research in the brief."
      runLabel={hasResearchNeeded ? 'Start Research' : 'Complete (no research needed)'}
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
